import type {Permission, PermissionType} from '@hconnect/apiclient'
import {
  getPlantIdsPerCountryMap,
  useUpmPlantsContext,
  type PlantIdsPerCountryMap
} from '@hconnect/upm'
import {usePermissionsQuery} from '@hproduce/permissions'
import {useMemo} from 'react'

export const getUpmIdsForPermissions = (
  permissions: Permission[],
  plantIdsForCountryMap: PlantIdsPerCountryMap,
  plantIdToUpmId: Map<string, string>,
  allUpmIds: Set<string>
) => {
  const upmIdsSet = new Set<string>()

  const resolveByCountry = (countryId: string) => {
    plantIdsForCountryMap[countryId]?.forEach((plantId) => {
      const resolvedUpmId = plantIdToUpmId.get(plantId)
      if (resolvedUpmId) {
        upmIdsSet.add(resolvedUpmId)
      }
    })
  }

  permissions.forEach(({dataScope}) => {
    try {
      const parsed = JSON.parse(dataScope) as Record<string, unknown>

      if (typeof parsed.upmId === 'string') {
        if (allUpmIds.has(parsed.upmId)) {
          upmIdsSet.add(parsed.upmId)
        }
      } else if (typeof parsed.plantId === 'string') {
        const resolvedUpmId = plantIdToUpmId.get(parsed.plantId)
        if (resolvedUpmId) {
          upmIdsSet.add(resolvedUpmId)
        }
      } else if (typeof parsed.countryId === 'string') {
        resolveByCountry(parsed.countryId)
      }
    } catch {
      // skip unparseable dataScope
    }
  })

  return Array.from(upmIdsSet)
}

export const useRiskSurveyPlantsByPermission = (permission: PermissionType) => {
  const {data: permissions = []} = usePermissionsQuery()
  const filteredPermissions = permissions.filter((p) => p.permissionType === permission)

  const upmPlants = useUpmPlantsContext()
  const plantIdsForCountryMap = getPlantIdsPerCountryMap(upmPlants)
  const plantIdToUpmId = useMemo(
    () => new Map(Object.values(upmPlants).map((plant) => [plant.plantId, plant.upmId])),
    [upmPlants]
  )
  const allUpmIds = useMemo(
    () => new Set(Object.values(upmPlants).map((plant) => plant.upmId)),
    [upmPlants]
  )

  const plantIds = getUpmIdsForPermissions(
    filteredPermissions,
    plantIdsForCountryMap,
    plantIdToUpmId,
    allUpmIds
  )

  return {plantIds}
}
